import { InvisibleButton } from "../Buttons/Buttons";
import type { ProductFiltersMainSchema } from "../../types/types";
import { FiltersWrapper } from "../../layout/publicPageLayout";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import type { ChipType } from "../Chips/Chips";
import { endpoints } from "../../endpoints";
import { toTitleCase, useMediaQueries, useStoreState } from "../../util/util";
import noop from "lodash/noop";
import { useHistory, useLocation } from "react-router-dom";
import { CaretDownIcon, XIcon } from "../Icons/Icons";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { getProductStatusText } from "../Status/Status";
import type { ProductStatusType } from "../../types/types.PIM";
import { screenSize } from "../../theme";
import { loadFilters, saveFilters } from "../../util/filterStateManager";

export function chipsToStringArray(chips: ChipType[]) {
  return chips.map((chip) => chip.name);
}

interface ICustomFiltersWrapper {
  showAll: boolean;
}

const CustomFiltersWrapper = styled(FiltersWrapper)<ICustomFiltersWrapper>`
  background-image: none;
  background-position: left 6px;
  padding-left: 0;
  overflow: auto;
  max-height: 180px;
  background-size: 18px;
  margin: 0;
  transition: all 200ms ease;
  width: auto !important;
  @media ${screenSize.small} {
    max-height: 120px;
  }
`;

const FiltersListArea = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  justify-content: flex-start;
  align-items: flex-start;
`;
const EmptyFiltersPlaceholder = styled.div`
  width: 100%;
  /* height: 30px; */
  margin: 0 0 35px;
`;

const FilterItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  border: ${({ theme }) => theme.resolve("border_sm")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  font-size: ${({ theme }) => theme.fontSizes.small};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
  margin: 0 ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_xs")} 0;
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  button {
    padding: 2px;
    margin-left: 2px;
    display: flex;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  margin-top: 3px;
`;

type FilterAreaProps = {
  resetOffset?: React.Dispatch<React.SetStateAction<number>>;
  clearFilter: () => void;
  filtersList: string[];
  isPublic: boolean;
};

export function SelectedFiltersArea({
  resetOffset = noop,
  clearFilter,
  filtersList,
}: FilterAreaProps) {
  const location = useLocation();
  const { pathname } = location;

  const { storefront_id } = useStoreState();
  const [showAllFilters, setShowAllFilters] = useState(false);
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();

  const wrapperRef = useRef(document.createElement("div"));
  const { isLargeScreen } = useMediaQueries();

  const getFiltersFromURL = () => {
    const allFilters = loadFilters(location);
    const parms: { type: string; label: string; originalValue: string }[] = [];

    Object.entries(allFilters).forEach(([key, values]) => {
      if (filtersList?.indexOf(key) > -1 && Array.isArray(values)) {
        values.forEach((value) => {
          // Translate status values to display text
          const displayLabel =
            key === "status"
              ? getProductStatusText(value as ProductStatusType, t)
              : value;
          parms.push({
            type: key,
            label:
              displayLabel === t("Unassigned")
                ? `${t("Unassigned")} (${toTitleCase(key.replace(/_/g, " "))})`
                : displayLabel,
            originalValue: value, // Keep original value for URL operations
          });
        });
      }
    });

    return parms.length > 0 ? parms : undefined;
  };

  const [query] = useQueryParams({
    active_tag_view: StringParam,
    q: StringParam,
  });

  const {
    data: filters,
    error: filtersError,
    mutate: mutateFilters,
  } = useSWR<ProductFiltersMainSchema, Error>(
    endpoints.v2_storefronts_id_products_filters(storefront_id)
  );

  const handleFilterClick = (filter: {
    type: string;
    label: string;
    originalValue: string;
  }) => {
    // Load current filters
    const currentFilters = loadFilters(location);

    // Remove the clicked filter value
    const updatedFilters: { [key: string]: string[] } = { ...currentFilters };

    if (updatedFilters[filter.type]) {
      updatedFilters[filter.type] = updatedFilters[filter.type].filter(
        (v) => v !== filter.originalValue
      );

      // If no values left for this filter type, remove the key entirely
      if (updatedFilters[filter.type].length === 0) {
        delete updatedFilters[filter.type];
      }
    }

    // Add active_tag_view for UI state
    updatedFilters.active_tag_view = [filter.type];

    // Save updated filters
    saveFilters(updatedFilters, history, pathname, {
      q: query?.q || undefined,
    });

    resetOffset(0);
  };

  const handleClearFilters = () => {
    // Clear all filters, keeping only non-filter params
    const clearedFilters: { [key: string]: string[] } = {};

    if (query.active_tag_view) {
      clearedFilters.active_tag_view = [query.active_tag_view];
    }

    saveFilters(clearedFilters, history, pathname, {
      q: query?.q || undefined,
    });

    resetOffset(0);
    clearFilter();
  };

  useEffect(() => {
    mutateFilters();
  }, [query, mutateFilters]);

  if (filtersError) {
    // Search still works if filters don't load.
    // This maintains the layout of the page in case of error.
    return <EmptyFiltersPlaceholder />;
  } else if (filters?.filters) {
    // Handle case that happened once on staging where all filters were empty arrays.
    // This might theoretically be possible if a tenant is activated and no filters
    // are added.
    const filter_labels = getFiltersFromURL();
    if (filter_labels === undefined || filter_labels.length === 0) return null;
    else {
      return (
        <FiltersContainer>
          <CustomFiltersWrapper showAll={showAllFilters}>
            <FiltersListArea ref={wrapperRef}>
              {filter_labels?.map(
                (filter, index) =>
                  (showAllFilters ||
                    (!showAllFilters && index < (isLargeScreen ? 3 : 10))) && (
                    <FilterItemWrapper key={index}>
                      <span>{filter.label}</span>
                      <InvisibleButton
                        onClick={() => handleFilterClick(filter)}
                      >
                        <XIcon width={16} height={16} />
                      </InvisibleButton>
                    </FilterItemWrapper>
                  )
              )}
              {!showAllFilters &&
                filter_labels?.length > (isLargeScreen ? 3 : 10) && (
                  <span
                    onClick={() => setShowAllFilters(true)}
                    style={{
                      fontSize: theme.fontSizes.small,
                      cursor: "pointer",
                      marginTop: "7px",
                      marginLeft: "5px",
                    }}
                  >
                    and {filter_labels?.length - (isLargeScreen ? 3 : 10)}{" "}
                    more...
                  </span>
                )}
            </FiltersListArea>
          </CustomFiltersWrapper>
          <IconsContainer>
            {filter_labels?.length > (isLargeScreen ? 3 : 10) && (
              <InvisibleButton
                onClick={() => setShowAllFilters((prev) => !prev)}
                style={{
                  transform: showAllFilters ? "rotate(180deg)" : "none",
                }}
              >
                <CaretDownIcon width={16} height={16} />
              </InvisibleButton>
            )}

            {filter_labels?.[0]?.label && (
              <InvisibleButton
                onClick={() => {
                  handleClearFilters();
                }}
              >
                <XIcon
                  width={19}
                  height={19}
                  fill={theme.destructiveTextColor}
                />
              </InvisibleButton>
            )}
          </IconsContainer>
        </FiltersContainer>
      );
    }
  } else return null;
}
